import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { financeService } from '../services/financeService';
import { analyticsService } from '../services/analyticsService';
import { withCache } from '../services/cacheService';
import RafflePerformanceAnalytics from '../components/RafflePerformanceAnalytics';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Chip,
  Avatar,
  CircularProgress,
  Divider,
  useTheme,
  useMediaQuery,
  Fade,
  Tabs,
  Tab,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import AsyncSearchSelect from '../components/AsyncSearchSelect';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  DateRange as DateRangeIcon,
  Category as CategoryIcon,
  Timeline as TimelineIcon,
  DonutSmall as DonutSmallIcon,
  BarChart as BarChartIcon,
  ShowChart as ShowChartIcon,
  TableChart as TableChartIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';

// ── Componente DRE ────────────────────────────────────────────────────────────

const DRE_ROWS = [
  { label: 'RECEITA OPERACIONAL', type: 'section' },
  { label: 'Receita Bruta', key: 'receita_bruta', type: 'item' },
  { label: '(-) Deduções e Abatimentos', key: 'deducoes_abatimentos', type: 'deduction' },
  { label: '(=) RECEITA LÍQUIDA', key: 'receita_liquida', type: 'result' },
  { label: 'CUSTOS DOS SERVIÇOS PRESTADOS (CSP)', type: 'section' },
  { label: '(-) Custo Variável', key: 'csp', type: 'deduction' },
  { label: '(=) LUCRO BRUTO', key: 'lucro_bruto', type: 'result' },
  { label: '(-) DESPESAS OPERACIONAIS', type: 'section' },
  { label: 'Despesas com Vendas', key: 'despesas_vendas', type: 'deduction' },
  { label: 'Despesas Administrativas', key: 'despesas_administrativas', type: 'deduction' },
  { label: 'Despesas Financeiras', key: 'despesas_financeiras', type: 'deduction' },
  { label: 'Total Despesas Operacionais', key: 'total_despesas_operacionais', type: 'subtotal' },
  { label: '(=) RESULTADO ANTES IRPJ E CSLL', key: 'resultado_antes_irpj', type: 'result' },
  { label: '(-) PROVISÕES PARA IRPJ E CSLL', type: 'section' },
  { label: 'Provisões IRPJ / CSLL', key: 'provisoes_irpj_csll', type: 'deduction' },
  { label: '(=) RESULTADO LÍQUIDO', key: 'resultado_liquido', type: 'final' },
];

// Chaves que possuem detalhes de subclassificação
const DETAIL_KEYS = new Set([
  'receita_bruta', 'deducoes_abatimentos', 'csp',
  'despesas_vendas', 'despesas_administrativas', 'despesas_financeiras',
  'provisoes_irpj_csll',
]);

// Chaves cujos valores são custos/deduções (exibidos com parênteses)
const DEDUCTION_KEYS = new Set([
  'deducoes_abatimentos', 'csp',
  'despesas_vendas', 'despesas_administrativas', 'despesas_financeiras',
  'provisoes_irpj_csll',
]);

const DRETab = ({ dreData, dreLoading, dreExporting, onLoad, onExport, formatCurrency, productFilterLabel }) => {
  const theme = useTheme();

  const border = `1px solid ${theme.palette.divider}`;
  const monoSx = { fontFamily: 'monospace' };

  const fmtAbs = (v) => formatCurrency(Math.abs(parseFloat(v ?? 0)));
  const fmtDeduction = (v) => `(${fmtAbs(v)})`;
  const fmtResult = (v) => {
    const n = parseFloat(v ?? 0);
    return n < 0 ? `(${fmtAbs(n)})` : fmtAbs(n);
  };
  const resultColor = (v) =>
    parseFloat(v ?? 0) >= 0 ? theme.palette.success.main : theme.palette.error.main;

  const tdBase = { borderBottom: border, py: 0.75 };

  const buildRows = () => {
    if (!dreData) return [];
    const rows = [];

    for (const row of DRE_ROWS) {
      // ── Cabeçalho de seção ──────────────────────────────────────────────
      if (row.type === 'section') {
        rows.push(
          <Box component="tr" key={row.label}>
            <Box component="td" colSpan={3} sx={{
              ...tdBase,
              py: 0.5, px: 2,
              backgroundColor: theme.palette.primary.main + '18',
              color: theme.palette.primary.main,
              fontWeight: 700, fontSize: '0.8rem',
              textTransform: 'uppercase', letterSpacing: 0.5,
            }}>
              {row.label}
            </Box>
          </Box>
        );
        continue;
      }

      const val = parseFloat(dreData?.[row.key] ?? 0);
      const details = dreData?.details?.[row.key] ?? [];
      const hasDetails = DETAIL_KEYS.has(row.key) && details.length > 0;
      const isDeduction = DEDUCTION_KEYS.has(row.key);

      // ── Resultado calculado ─────────────────────────────────────────────
      if (row.type === 'result' || row.type === 'final') {
        const isFinal = row.type === 'final';
        const bg = isFinal ? (val >= 0 ? '#e8f5e9' : '#ffebee') : theme.palette.grey[100];
        const fs = isFinal ? '1rem' : '0.875rem';
        rows.push(
          <Box component="tr" key={row.key}>
            <Box component="td" colSpan={2} sx={{ ...tdBase, px: 2, backgroundColor: bg, fontWeight: 700, fontSize: fs }}>{row.label}</Box>
            <Box component="td" sx={{ ...tdBase, ...monoSx, pr: 2, textAlign: 'right', backgroundColor: bg, fontWeight: 700, fontSize: fs, color: resultColor(val) }}>
              {fmtResult(val)}
            </Box>
          </Box>
        );
        continue;
      }

      // ── Subtotal (Total Despesas Operacionais) ──────────────────────────
      if (row.type === 'subtotal') {
        rows.push(
          <Box component="tr" key={row.key}>
            <Box component="td" colSpan={2} sx={{ ...tdBase, px: 2, fontWeight: 700, fontSize: '0.875rem' }}>{row.label}</Box>
            <Box component="td" sx={{ ...tdBase, ...monoSx, pr: 2, textAlign: 'right', fontWeight: 700 }}>{fmtDeduction(val)}</Box>
          </Box>
        );
        continue;
      }

      // ── Linha com detalhes de subclassificações ─────────────────────────
      if (hasDetails) {
        // Cabeçalho da categoria
        rows.push(
          <Box component="tr" key={`${row.key}_hdr`}>
            <Box component="td" colSpan={3} sx={{ ...tdBase, px: 2, pt: 1.5, fontWeight: 700, fontSize: '0.875rem' }}>
              {row.label}
            </Box>
          </Box>
        );

        // Sub-linhas de cada subclassificação
        details.forEach((d, i) => {
          const detailFmt = isDeduction ? fmtDeduction(d.total) : fmtAbs(d.total);
          rows.push(
            <Box component="tr" key={`${row.key}_d${i}`}>
              <Box component="td" sx={{ ...tdBase, pl: 4, pr: 2, fontSize: '0.8rem', color: theme.palette.text.secondary }}>
                {d.name}
              </Box>
              <Box component="td" sx={{ ...tdBase, ...monoSx, pr: 2, textAlign: 'right', fontSize: '0.8rem', color: theme.palette.text.secondary }}>
                {detailFmt}
              </Box>
              <Box component="td" sx={{ ...tdBase }} />
            </Box>
          );
        });

        // Total da categoria (coluna Total)
        const totalFmt = isDeduction ? fmtDeduction(val) : fmtAbs(val);
        rows.push(
          <Box component="tr" key={`${row.key}_tot`}>
            <Box component="td" sx={{ ...tdBase, px: 2 }} />
            <Box component="td" sx={{ ...tdBase }} />
            <Box component="td" sx={{ ...tdBase, ...monoSx, pr: 2, textAlign: 'right', fontWeight: 700, borderTop: `2px solid ${theme.palette.divider}` }}>
              {totalFmt}
            </Box>
          </Box>
        );
        continue;
      }

      // ── Linha simples sem detalhes ──────────────────────────────────────
      const fmtd = isDeduction ? fmtDeduction(val) : fmtAbs(val);
      rows.push(
        <Box component="tr" key={row.key}>
          <Box component="td" colSpan={2} sx={{ ...tdBase, pl: isDeduction ? 4 : 2, pr: 2, fontSize: '0.875rem' }}>{row.label}</Box>
          <Box component="td" sx={{ ...tdBase, ...monoSx, pr: 2, textAlign: 'right' }}>{fmtd}</Box>
        </Box>
      );
    }

    return rows;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h6">DRE Gerencial</Typography>
          {productFilterLabel && (
            <Typography variant="body2" color="text.secondary">
              Produto / Sorteio: {productFilterLabel}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={dreLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={onLoad}
            disabled={dreLoading}
          >
            {dreLoading ? 'Carregando...' : 'Gerar DRE'}
          </Button>
          <Button
            variant="contained"
            startIcon={dreExporting ? <CircularProgress size={16} /> : <PdfIcon />}
            onClick={onExport}
            disabled={dreExporting || !dreData}
            color="primary"
          >
            {dreExporting ? 'Exportando...' : 'Exportar PDF'}
          </Button>
        </Box>
      </Box>

      {!dreData && !dreLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Clique em <strong>Gerar DRE</strong> para calcular a Demonstração do Resultado do Exercício com os filtros aplicados (período e produto selecionados no painel acima).
        </Alert>
      )}

      {dreLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {dreData && !dreLoading && (
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <Box component="thead">
              <Box component="tr">
                {[['Descrição', '58%', 'left', 2], ['Saldo', '22%', 'right', 2], ['Total', '20%', 'right', 2]].map(([label, width, align, pr]) => (
                  <Box key={label} component="th" sx={{
                    width, textAlign: align, pr: align === 'right' ? pr : 0, pl: align === 'left' ? pr : 0,
                    py: 1, fontWeight: 700, fontSize: '0.8rem',
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.secondary,
                  }}>
                    {label}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {buildRows()}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const FinancialAnalytics = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, hasPermission } = useAuth();
  const { showNotification, showLoading } = useNotification();

  // Estados principais
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);

  // DRE
  const [dreData, setDreData] = useState(null);
  const [dreLoading, setDreLoading] = useState(false);
  const [dreExporting, setDreExporting] = useState(false);

  // Estados de dados
  const [financialData, setFinancialData] = useState({
    summary: null,
    transactions: [],
    trends: [],
    categoryBreakdown: [],
    monthlyComparison: [],
    cashFlow: []
  });
  
  const [products, setProducts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [productFilterLabel, setProductFilterLabel] = useState('');
  const [enums, setEnums] = useState({
    transactionTypes: [],
    transactionClassifications: []
  });

  // Estados de filtros
  const [filters, setFilters] = useState({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
    transactionType: '',
    classification: '',
    productId: '',
    assetId: '',
    isVerified: '',
    createdBy: '',
    isPreparationCost: '',
    minAmount: '',
    maxAmount: ''
  });

  // Cores para gráficos - Paleta harmoniosa e contrastante
  const chartColors = [
    '#1976d2', // Azul
    '#4caf50', // Verde
    '#ff9800', // Laranja
    '#f44336', // Vermelho
    '#9c27b0', // Roxo
    '#00bcd4', // Ciano
    '#795548', // Marrom
    '#607d8b', // Azul acinzentado
    '#e91e63', // Rosa
    '#8bc34a', // Verde claro
    '#ffc107', // Âmbar
    '#673ab7'  // Roxo escuro
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (Object.values(filters).some(f => f !== '')) {
      loadFinancialData();
    }
  }, [filters]);

  // Keep label in sync for Product/Sorteio filter
  useEffect(() => {
    const id = String(filters.productId ?? '').trim();
    if (!id) {
      if (productFilterLabel) setProductFilterLabel('');
      return;
    }
    const match = (products || []).find(p => String(p.id ?? '').trim() === id);
    if (match?.title && match.title !== productFilterLabel) {
      setProductFilterLabel(match.title);
    }
  }, [filters.productId, products, productFilterLabel]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadEnums(),
        loadProducts(),
        loadAssets(),
        loadUsers(),
        loadFinancialData()
      ]);
    } catch (err) {
      setError(err.message);
      showNotification('Erro ao carregar dados iniciais', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadEnums = async () => {
    try {
      // Cache de 1 hora para enums
      const [types, classifications] = await Promise.all([
        withCache('financial_enums_types', () => financeService.getTransactionTypes(), 60 * 60 * 1000),
        withCache('financial_enums_classifications', () => financeService.getTransactionClassifications(), 60 * 60 * 1000)
      ]);
      setEnums({
        transactionTypes: types || [],
        transactionClassifications: classifications || []
      });
    } catch (err) {
      console.error('Erro ao carregar enums:', err);
    }
  };

  const loadProducts = async () => {
    try {
      // Carrega produtos somente se tiver permissão
      if (!hasPermission || !hasPermission('finance.products.read')) {
        setProducts([]);
        return;
      }
      // Cache de 10 minutos para produtos
      const productsData = await withCache(
        'financial_products_list',
        () => financeService.getAvailableProducts(),
        10 * 60 * 1000
      );
      setProducts(productsData || []);
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
    }
  };

  const loadAssets = async () => {
    try {
      // Cache de 5 minutos para ativos
      const assetsData = await withCache(
        'financial_assets_list',
        () => financeService.getAssets(),
        5 * 60 * 1000
      );
      setAssets(assetsData || []);
    } catch (err) {
      console.error('Erro ao carregar ativos:', err);
    }
  };

  const loadUsers = async () => {
    try {
      // Assumindo que existe um serviço para carregar usuários
      // Vou implementar isso se não existir
      const usersData = await financeService.getUsers?.() || [];
      setUsers(usersData);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      setUsers([]); // Fallback para array vazio
    }
  };

  const loadFinancialData = async () => {
    try {
      const params = {
        start_date: filters.startDate ? new Date(filters.startDate).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) : undefined,
        end_date: filters.endDate ? new Date(filters.endDate).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) : undefined,
        transaction_type: filters.transactionType || undefined,
        classification: filters.classification || undefined,
        product_id: filters.productId || undefined,
        asset_id: filters.assetId || undefined,
        created_by: filters.createdBy || undefined,
        is_verified: filters.isVerified !== '' ? filters.isVerified === 'true' : undefined,
        is_preparation_cost: filters.isPreparationCost !== '' ? filters.isPreparationCost === 'true' : undefined,
        min_amount: filters.minAmount ? parseFloat(filters.minAmount) : undefined,
        max_amount: filters.maxAmount ? parseFloat(filters.maxAmount) : undefined
      };

      const [summary, summaryByProduct, transactions] = await Promise.all([
        financeService.getFinancialSummary(params),
        financeService.getFinancialSummaryByProduct(params),
        financeService.getTransactions(params)
      ]);

      // Processar dados para gráficos
      const processedData = processFinancialData(transactions, summary, summaryByProduct);
      
      setFinancialData({
        summary,
        transactions,
        ...processedData
      });

    } catch (err) {
      console.error('Erro ao carregar dados financeiros:', err);
      showNotification('Erro ao carregar análises financeiras', 'error');
    }
  };

  const processFinancialData = (transactions, summary, summaryByProduct) => {
    // Regra de negócio: classificações semânticas fora do P&L (ex.: retirada de lucros,
    // ativo imobilizado) não entram nos cálculos de análise.
    const analysisTransactions = (transactions || []).filter(
      (t) => !['retirada_lucros', 'ativo_imobilizado'].includes(t?.classification)
    );

    // Processar tendências temporais
    const trends = processTransactionTrends(analysisTransactions);
    
    // Processar breakdown por categoria
    const categoryBreakdown = processCategories(analysisTransactions);
    
    // Processar comparação mensal
    const monthlyComparison = processMonthlyData(analysisTransactions);
    
    // Processar fluxo de caixa
    const cashFlow = processCashFlow(analysisTransactions);

    return {
      trends,
      categoryBreakdown,
      monthlyComparison,
      cashFlow
    };
  };

  const processTransactionTrends = (transactions) => {
    const dailyData = {};
    
    transactions.forEach(transaction => {
      const date = format(new Date(transaction.transaction_date), 'yyyy-MM-dd');
      if (!dailyData[date]) {
        dailyData[date] = { date, receita: 0, gastos: 0, despesa: 0, custo: 0 };
      }
      
      const amount = parseFloat(transaction.amount);
      if (transaction.classification === 'receita') {
        dailyData[date].receita += amount;
      } else if (transaction.classification.includes('despesa') || transaction.classification === 'imposto') {
        dailyData[date].despesa += amount;
        dailyData[date].gastos += amount;
      } else if (transaction.classification.includes('custo')) {
        dailyData[date].custo += amount;
        dailyData[date].gastos += amount;
      }
    });

    return Object.values(dailyData).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const processCategories = (transactions) => {
    const categories = {};
    
    transactions.forEach(transaction => {
      const category = getClassificationLabel(transaction.classification);
      if (!categories[category]) {
        categories[category] = 0;
      }
      categories[category] += parseFloat(transaction.amount);
    });

    return Object.entries(categories).map(([name, value]) => ({ name, value }));
  };

  const processMonthlyData = (transactions) => {
    const monthlyData = {};
    
    transactions.forEach(transaction => {
      const month = format(new Date(transaction.transaction_date), 'yyyy-MM');
      if (!monthlyData[month]) {
        monthlyData[month] = { month, receitas: 0, gastos: 0, despesas: 0, custos: 0, lucro: 0 };
      }
      
      const amount = parseFloat(transaction.amount);
      if (transaction.classification === 'receita') {
        monthlyData[month].receitas += amount;
      } else if (transaction.classification.includes('despesa') || transaction.classification === 'imposto') {
        monthlyData[month].despesas += amount;
        monthlyData[month].gastos += amount;
      } else if (transaction.classification.includes('custo')) {
        monthlyData[month].custos += amount;
        monthlyData[month].gastos += amount;
      }
    });

    Object.values(monthlyData).forEach(data => {
      data.lucro = data.receitas - data.gastos;
    });

    return Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
  };

  const processCashFlow = (transactions) => {
    let balance = 0;
    const cashFlowData = [];
    
    const sortedTransactions = [...transactions].sort((a, b) => 
      new Date(a.transaction_date) - new Date(b.transaction_date)
    );

    sortedTransactions.forEach(transaction => {
      const amount = parseFloat(transaction.amount);
      if (transaction.classification === 'receita') {
        balance += amount;
      } else {
        balance -= amount;
      }
      
      cashFlowData.push({
        date: format(new Date(transaction.transaction_date), 'dd/MM'),
        balance,
        transaction: amount,
        type: transaction.classification
      });
    });

    return cashFlowData;
  };

  const getClassificationLabel = (classification) => {
    const labels = {
      'custo_fixo': 'Custo Fixo',
      'custo_variavel': 'Custo Variável',
      'despesa_fixa': 'Despesa Fixa',
      'despesa_variavel': 'Despesa Variável',
      'receita': 'Receita',
      'imposto': 'Imposto',
      'retirada_lucros': 'Retirada lucros',
      'ativo_imobilizado': 'Ativo imobilizado'
    };
    return labels[classification] || classification;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const handleApplyFilters = () => {
    loadFinancialData();
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: subDays(new Date(), 30),
      endDate: new Date(),
      transactionType: '',
      classification: '',
      productId: '',
      assetId: '',
      isVerified: '',
      createdBy: '',
      isPreparationCost: '',
      minAmount: '',
      maxAmount: ''
    });
  };

  const handleGeneratePDF = async () => {
    try {
      showLoading('Gerando relatório financeiro...');
      
      // Debug: log the summary data being sent
      console.log('Summary data for PDF:', financialData.summary);
      
      const reportData = {
        title: 'Relatório Financeiro Analítico',
        period: `${format(filters.startDate, 'dd/MM/yyyy')} - ${format(filters.endDate, 'dd/MM/yyyy')}`,
        filters: {
          transactionType: filters.transactionType,
          classification: filters.classification,
          productId: filters.productId,
          assetId: filters.assetId,
          isVerified: filters.isVerified
        },
        summary: financialData.summary,
        transactions: financialData.transactions,
        charts: {
          trends: financialData.trends,
          categories: financialData.categoryBreakdown,
          monthly: financialData.monthlyComparison,
          cashFlow: financialData.cashFlow
        }
      };

      console.log('Complete report data:', reportData);
      await financeService.generateFinancialReport(reportData);
      showNotification('Relatório gerado com sucesso!', 'success');
    } catch (err) {
      showNotification('Erro ao gerar relatório', 'error');
      console.error('Erro ao gerar PDF:', err);
    }
  };

  const loadDRE = async () => {
    try {
      setDreLoading(true);
      const params = {
        startDate: filters.startDate ? format(filters.startDate, 'yyyy-MM-dd') : undefined,
        endDate: filters.endDate ? format(filters.endDate, 'yyyy-MM-dd') : undefined,
        productId: filters.productId || undefined,
      };
      const data = await financeService.getDRE(params);
      setDreData(data);
    } catch (err) {
      showNotification('Erro ao carregar DRE', 'error');
      console.error('Erro ao carregar DRE:', err);
    } finally {
      setDreLoading(false);
    }
  };

  const handleExportDREPdf = async () => {
    try {
      setDreExporting(true);
      await financeService.exportDREPdf({
        startDate: filters.startDate ? format(filters.startDate, 'yyyy-MM-dd') : undefined,
        endDate: filters.endDate ? format(filters.endDate, 'yyyy-MM-dd') : undefined,
        productId: filters.productId || undefined,
        productName: productFilterLabel || undefined,
      });
      showNotification('DRE exportada com sucesso!', 'success');
    } catch (err) {
      showNotification('Erro ao exportar DRE', 'error');
      console.error('Erro ao exportar DRE:', err);
    } finally {
      setDreExporting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  // Verificar permissões
  if (!hasPermission('analytics.finance.read')) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Acesso negado. Você não tem permissão para visualizar análises financeiras.
        </Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold" color="primary">
                Analytics Financeiro
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Análise completa da performance financeira
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadFinancialData}
                size="large"
              >
                Atualizar
              </Button>
              <Button
                variant="contained"
                startIcon={<PdfIcon />}
                onClick={handleGeneratePDF}
                size="large"
                color="secondary"
              >
                Baixar PDF
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Filtros */}
        <Paper sx={{ p: 3, mb: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <FilterListIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Filtros de Análise
            </Typography>
          </Stack>
          
          <Grid container spacing={3}>
            {/* Primeira linha - Filtros principais */}
            <Grid item xs={12} sm={6} md={2.4}>
              <DatePicker
                label="Data Inicial"
                value={filters.startDate}
                onChange={(newValue) => setFilters({ ...filters, startDate: newValue })}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2.4}>
              <DatePicker
                label="Data Final"
                value={filters.endDate}
                onChange={(newValue) => setFilters({ ...filters, endDate: newValue })}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={filters.transactionType}
                  onChange={(e) => setFilters({ ...filters, transactionType: e.target.value })}
                  label="Tipo"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {enums.transactionTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.value === 'entrada' ? 'Entrada' : 'Saída'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Classificação</InputLabel>
                <Select
                  value={filters.classification}
                  onChange={(e) => setFilters({ ...filters, classification: e.target.value })}
                  label="Classificação"
                >
                  <MenuItem value="">Todas</MenuItem>
                  {enums.transactionClassifications.map(classification => (
                    <MenuItem key={classification.value} value={classification.value}>
                      {getClassificationLabel(classification.value)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.isVerified}
                  onChange={(e) => setFilters({ ...filters, isVerified: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="true">Verificadas</MenuItem>
                  <MenuItem value="false">Não Verificadas</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Segunda linha - Filtros avançados */}
            <Grid item xs={12} sm={6} md={2.4}>
              <AsyncSearchSelect
                label="Produto/Sorteio"
                value={filters.productId}
                selectedLabel={productFilterLabel}
                onChange={(id, option) => {
                  const nextId = String(id ?? '').trim();
                  setFilters(prev => ({ ...prev, productId: nextId }));
                  setProductFilterLabel(option?.label || '');
                }}
                fetchPage={({ q, offset, limit }) => financeService.searchProducts({ q, offset, limit })}
                limit={50}
                placeholder="Buscar sorteio..."
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <AsyncSearchSelect
                label="Ativo"
                value={filters.assetId}
                onChange={(id) => setFilters({ ...filters, assetId: id })}
                fetchPage={({ q, offset, limit }) => financeService.searchAssets({ q, offset, limit })}
                limit={50}
                placeholder="Buscar ativo..."
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Criado por</InputLabel>
                <Select
                  value={filters.createdBy}
                  onChange={(e) => setFilters({ ...filters, createdBy: e.target.value })}
                  label="Criado por"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {users.map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name || user.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                label="Valor Mínimo"
                type="number"
                size="small"
                value={filters.minAmount}
                onChange={(e) => setFilters({ ...filters, minAmount: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                label="Valor Máximo"
                type="number"
                size="small"
                value={filters.maxAmount}
                onChange={(e) => setFilters({ ...filters, maxAmount: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            {/* Terceira linha - Ações e filtros especiais */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de Custo</InputLabel>
                <Select
                  value={filters.isPreparationCost}
                  onChange={(e) => setFilters({ ...filters, isPreparationCost: e.target.value })}
                  label="Tipo de Custo"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="true">Custos de Preparação</MenuItem>
                  <MenuItem value="false">Outros Custos</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleApplyFilters}
                  size="small"
                  sx={{ minHeight: 40 }}
                >
                  Aplicar
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  size="small"
                  sx={{ minHeight: 40 }}
                >
                  Limpar
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Cards de Resumo */}
        {financialData.summary && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={600}>
                <Card elevation={0} sx={{ 
                  border: '1px solid',
                  borderColor: 'success.light',
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
                  transition: 'all 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                        <TrendingUpIcon fontSize="large" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Receita Total
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="success.main">
                          {formatCurrency(financialData.summary.total_revenue || 0)}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={800}>
                <Card elevation={0} sx={{ 
                  border: '1px solid',
                  borderColor: 'error.light',
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(244, 67, 54, 0.05) 100%)',
                  transition: 'all 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ bgcolor: 'error.main', width: 56, height: 56 }}>
                        <TrendingDownIcon fontSize="large" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Gastos Totais
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="error.main">
                          {formatCurrency(
                            Number(financialData.summary.total_expenses || 0) + 
                            Number(financialData.summary.total_costs || 0)
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Despesas + Custos
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={1000}>
                <Card elevation={0} sx={{ 
                  border: '1px solid',
                  borderColor: 'primary.light',
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0.05) 100%)',
                  transition: 'all 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                        <AccountBalanceIcon fontSize="large" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Lucro Líquido
                        </Typography>
                        <Typography 
                          variant="h5" 
                          fontWeight="bold" 
                          color={financialData.summary.net_profit >= 0 ? 'success.main' : 'error.main'}
                        >
                          {formatCurrency(financialData.summary.net_profit || 0)}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={1200}>
                <Card elevation={0} sx={{ 
                  border: '1px solid',
                  borderColor: 'warning.light',
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 152, 0, 0.05) 100%)',
                  transition: 'all 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                        <AssessmentIcon fontSize="large" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Margem de Lucro
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" color="warning.main">
                          {(() => {
                            const revenue = financialData.summary.total_revenue || 0;
                            const margin = revenue > 0 ? 
                              ((financialData.summary.net_profit || 0) / revenue * 100) : 0;
                            return `${margin.toFixed(1)}%`;
                          })()}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          </Grid>
        )}

        {/* Tabs de Análises */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={currentTab} 
              onChange={(e, newValue) => setCurrentTab(newValue)}
              variant={isMobile ? 'scrollable' : 'fullWidth'}
              scrollButtons="auto"
            >
              <Tab 
                icon={<TimelineIcon />} 
                label="Tendências" 
                iconPosition="start"
              />
              <Tab 
                icon={<DonutSmallIcon />} 
                label="Categorias" 
                iconPosition="start"
              />
              <Tab 
                icon={<BarChartIcon />} 
                label="Comparação Mensal" 
                iconPosition="start"
              />
              <Tab 
                icon={<ShowChartIcon />} 
                label="Fluxo de Caixa" 
                iconPosition="start"
              />
              <Tab
                icon={<AssessmentIcon />}
                label="Performance de Sorteios"
                iconPosition="start"
              />
              <Tab
                icon={<TableChartIcon />}
                label="DRE"
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Conteúdo das Tabs */}
          <Box sx={{ p: 3 }}>
            {/* Tab 0 - Tendências */}
            {currentTab === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Tendências Temporais
                </Typography>
                <Box sx={{ height: 400, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={financialData.trends}>
                      <defs>
                        <linearGradient id="colorReceita" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#4caf50" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#4caf50" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorDespesa" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f44336" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#f44336" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `R$ ${(value/1000).toFixed(0)}k`}
                      />
                      <RechartsTooltip 
                        formatter={(value) => [formatCurrency(value), '']}
                        labelFormatter={(label) => `Data: ${label}`}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="receita"
                        stackId="1"
                        stroke="#4caf50"
                        fillOpacity={1}
                        fill="url(#colorReceita)"
                        name="Receita"
                      />
                      <Area
                        type="monotone"
                        dataKey="despesa"
                        stackId="2"
                        stroke="#f44336"
                        fillOpacity={1}
                        fill="url(#colorDespesa)"
                        name="Despesa"
                      />
                      <Area
                        type="monotone"
                        dataKey="custo"
                        stackId="2"
                        stroke="#ff9800"
                        fill="#ff9800"
                        name="Custo"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            )}

            {/* Tab 1 - Categorias */}
            {currentTab === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Breakdown por Categoria
                </Typography>
                <Box sx={{ height: 400, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={financialData.categoryBreakdown}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {financialData.categoryBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            )}

            {/* Tab 2 - Comparação Mensal */}
            {currentTab === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Comparação Mensal
                </Typography>
                <Box sx={{ height: 400, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={financialData.monthlyComparison}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `R$ ${(value/1000).toFixed(0)}k`}
                      />
                      <RechartsTooltip 
                        formatter={(value) => [formatCurrency(value), '']}
                      />
                      <Legend />
                      <Bar dataKey="receitas" fill="#4caf50" name="Receitas" />
                      <Bar dataKey="gastos" fill="#f44336" name="Gastos Totais" />
                      <Bar dataKey="lucro" fill="#2196f3" name="Lucro" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            )}

            {/* Tab 3 - Fluxo de Caixa */}
            {currentTab === 3 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Fluxo de Caixa Acumulado
                </Typography>
                <Box sx={{ height: 400, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={financialData.cashFlow}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `R$ ${(value/1000).toFixed(0)}k`}
                      />
                      <RechartsTooltip 
                        formatter={(value) => [formatCurrency(value), 'Saldo']}
                        labelFormatter={(label) => `Data: ${label}`}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="balance" 
                        stroke="#2196f3" 
                        strokeWidth={3}
                        name="Saldo Acumulado"
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            )}

            {/* Tab 4 - Performance de Sorteios */}
            {currentTab === 4 && (
              <RafflePerformanceAnalytics filters={filters} />
            )}

            {/* Tab 5 - DRE Gerencial */}
            {currentTab === 5 && (
              <DRETab
                dreData={dreData}
                dreLoading={dreLoading}
                dreExporting={dreExporting}
                onLoad={loadDRE}
                onExport={handleExportDREPdf}
                formatCurrency={formatCurrency}
                productFilterLabel={productFilterLabel}
              />
            )}
          </Box>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default FinancialAnalytics;